/**
 * BPMN renderer layout metrics — the single source of pixel constants for the
 * horizontal-BPMN process view.
 *
 * Values are lifted by value from the **committed** `BpmnView.tsx`
 * (`git show HEAD:packages/app-shell/src/process/BpmnView.tsx`) so the headless
 * scene composer and the live view stay pixel-identical. The live view keeps
 * its own local copies until it adopts this module (plan P5); until then this
 * is an unguarded, deliberate mirror — edit both together.
 *
 * Grid model: `bpmnLayout` places nodes on an integer grid; a cell is
 * `CW × CH` px, and a node's centre is `PAD + col·CW + CW/2` (see `cx`/`cy` in
 * the live view). Everything below is in CSS px.
 */

// ── Grid & canvas ───────────────────────────────────────────────────────────

/** Grid column width (px). */
export const CW = 190;
/** Grid track height (px). */
export const CH = 96;
/** Outer canvas padding on every side (px). */
export const PAD = 24;

// ── Lane rail (swimlanes & pools — plan 2026-07-07-bpmn-lanes-pools P4) ─────

/** Width of one rail cell (pool spine / lane-label column) — rotated text. */
export const LANE_RAIL_CELL_W = 30;
/** Lane band separator rule. */
export const LANE_SEPARATOR_STROKE = 1;
/** Pool outer border — heavier than the lane separator. */
export const POOL_BORDER_STROKE = 1.5;
export const POOL_BORDER_RADIUS = 6;
/** Rotated rail label font size. */
export const LANE_LABEL_SIZE = 12;
/** Px of band height consumed per label character (truncation budget). */
export const LANE_LABEL_CHAR_H = 7.2;
/** Breathing room subtracted from a rail band's height before it becomes the
 *  rotated label's width budget. */
export const LANE_LABEL_PAD = 12;

// ── Task / subprocess / annotation box ──────────────────────────────────────

export const TASK_W = 150;
export const TASK_H = 54;
/** Task/subprocess corner radius. */
export const TASK_RADIUS = 8;
/** Task/gateway stroke width — normal vs active-or-diff-accented. */
export const NODE_STROKE = 1.5;
export const NODE_STROKE_ACTIVE = 2.5;
/** Task title baseline offset below centre. */
export const TASK_LABEL_DY = 4;
/** Owner chip sits above the box's top-left corner. */
export const OWNER_CHIP_DY = 5;
/** Inbound message-catch envelope (top-right of a task). */
export const INBOUND_INSET_X = 20;
export const INBOUND_INSET_Y = 6;
export const INBOUND_W = 13;
export const INBOUND_H = 9;
export const INBOUND_STROKE = 1.1;
/** Compensation double-triangle marker, near the box's top edge. */
export const COMPENSATION_MARKER_DY = 12;
/** Subprocess `⊞` activity marker, near the box's bottom edge. */
export const SUBPROCESS_MARKER_DY = 9;

// ── System cylinder (aspect `replicated` render mode) ───────────────────────

/** Per-step replicated aspect place — a small data-store cylinder drawn half a
 *  track below its consuming task, joined by a short `assoc` stub. */
export const SYSTEM_W = 84;
export const SYSTEM_H = 38;
/** Elliptical cap radius (ry) as a fraction of width — mirrors the board card
 *  cylinder silhouette (`shapes.ts` CAP_W_FRAC). */
export const SYSTEM_CAP_RY = 8;
export const SYSTEM_STROKE = 1.5;
/** Label baseline below the cylinder's bottom edge. */
export const SYSTEM_LABEL_DY = 12;
/** Translucent fill = the tint at this alpha over the canvas. */
export const SYSTEM_FILL_ALPHA = 0.16;

// ── Dangling engine badge (aspect `engine @@key`, caller-derived) ───────────

/** A small aspect-value chip hung off a task's top-right corner by a short
 *  stub. Node-attached (not its own grid node), so it never claims a track. */
export const ENGINE_BADGE_W = 44;
export const ENGINE_BADGE_H = 15;
/** Corner radius of the rounded chip. */
export const ENGINE_BADGE_R = 3;
/** Chip centre offset from the task's top-right corner: right / up. */
export const ENGINE_BADGE_DX = 4;
export const ENGINE_BADGE_DY = 7;
/** Short connector stub length from the corner to the chip. */
export const ENGINE_STUB = 5;
export const ENGINE_STROKE = 1;
export const ENGINE_FILL_ALPHA = 0.16;

// ── Gateway diamond ─────────────────────────────────────────────────────────

/** Half-diagonal of the gateway diamond. */
export const GATE_R = 24;
/** Gateway label sits above the top vertex. */
export const GATE_LABEL_DY = 6;

// ── Event circle ────────────────────────────────────────────────────────────

export const EVENT_R = 18;
export const EVENT_STROKE = 1.5;
/** Owner-tinted event ring (live view: `oc ? 2 : 1.5`). */
export const EVENT_STROKE_OWNER = 2;
/** End events (and the active/scrubbed ring) draw a heavier circle. */
export const EVENT_STROKE_THICK = 3;
/** Intermediate/link/boundary events add an inner ring at `EVENT_R - inset`. */
export const EVENT_DOUBLE_RING_INSET = 4;
export const EVENT_DOUBLE_RING_STROKE = 1;
/** Terminate event's filled inner disc at `EVENT_R - inset`. */
export const EVENT_TERMINATE_INSET = 7;
/** Event label baseline below the circle. */
export const EVENT_LABEL_DY = 13;

// ── Text annotation (open bracket) ──────────────────────────────────────────

/** Horizontal reach of the open-bracket arms. */
export const ANNOTATION_BRACKET_W = 12;
export const ANNOTATION_STROKE = 1.2;
export const ANNOTATION_STROKE_ACTIVE = 2;
/** Annotation text baseline offset below centre / left padding. */
export const ANNOTATION_TEXT_DX = 8;
export const ANNOTATION_TEXT_DY = 3;
/** Annotation owner label offset from the top-left. */
export const ANNOTATION_OWNER_DY = 2;

// ── Frames (loop / try / dist / do bands behind the nodes) ──────────────────

/** Frame rect insets inside its grid span: `[x+INSET_X, y+INSET_Y]`, width
 *  shrunk by INSET_W, height by INSET_H. */
export const FRAME_INSET_X = 8;
export const FRAME_INSET_Y = 6;
export const FRAME_INSET_W = 16;
export const FRAME_INSET_H = 12;
export const FRAME_RADIUS = 10;
/** Frame label offset from the frame's top-left corner. */
export const FRAME_LABEL_DX = 8;
export const FRAME_LABEL_DY = 14;
/** Activity marker offset up from the frame's bottom edge. */
export const FRAME_MARKER_DY = 10;
/** Dash patterns by frame kind (`do` frames are solid — no dasharray). */
export const FRAME_DASH_DIST = "8 4 2 4";
export const FRAME_DASH_DEFAULT = "6 4";

// ── Edges ───────────────────────────────────────────────────────────────────

export const EDGE_STROKE = 1.5;
/** Horizontal offset of an orthogonal edge's elbow from the target/source. */
export const EDGE_ELBOW = 18;
/** Frame-anchored association (try frame → boundary event) departs from a
 *  point `[x0·CW + DX, y1·CH + CH - DY]` in the frame's lower-left. */
export const ASSOC_ANCHOR_DX = 28;
export const ASSOC_ANCHOR_DY = 10;
/** Failure `drop` edge's horizontal run sits `CH / DROP_ELBOW_DIVISOR` above
 *  the target. */
export const DROP_ELBOW_DIVISOR = 3;
/** `go` back-edge arc apex lift = `max(REENTRY_MIN_LIFT, |Δcol|·CW / DIVISOR)`;
 *  the canvas reserves this much headroom above the top track. */
export const REENTRY_MIN_LIFT = 48;
export const REENTRY_LIFT_DIVISOR = 6;
/** BPMN default-flow slash across the flow near an edge's source
 *  (`[sx+DX1, sy+DY1] → [sx+DX2, sy+DY2]`). */
export const DEFAULT_SLASH_DX1 = 8;
export const DEFAULT_SLASH_DY1 = 5;
export const DEFAULT_SLASH_DX2 = 16;
export const DEFAULT_SLASH_DY2 = 5;
/** Edge label baseline offset above the edge midpoint. */
export const EDGE_LABEL_DY = 6;
/** Dash patterns by edge style (`seq` is solid — no dasharray). */
export const EDGE_DASH = {
  seq: undefined,
  comp: "6 4",
  drop: "4 4",
  reentry: "6 4",
  assoc: "2 4",
} as const;

// ── Arrowhead markers ───────────────────────────────────────────────────────

/** Shared marker geometry (`viewBox 0 0 SIZE SIZE`, tip at `[REF_X, REF_Y]`). */
export const ARROW_VIEWBOX = 10;
export const ARROW_REF_X = 9;
export const ARROW_REF_Y = 5;
/** Filled solid-triangle head (sequence flow). */
export const ARROW_MARKER_SIZE = 7;
/** Hollow open head (comp/drop/reentry flows). */
export const ARROW_OPEN_MARKER_SIZE = 8;
export const ARROW_OPEN_STROKE = 1.5;

// ── Gateway & event glyph dimensions ────────────────────────────────────────

/** Gateway glyphs (×, +, ◯, ✳, event pentagon). */
export const GLYPH_XOR_ARM = 6;
export const GLYPH_XOR_STROKE = 2.4;
export const GLYPH_AND_ARM = 8;
export const GLYPH_AND_STROKE = 2.4;
export const GLYPH_OR_R = 7;
export const GLYPH_OR_STROKE = 2.4;
export const GLYPH_COMPLEX_ARM = 8;
export const GLYPH_COMPLEX_DIAG = 5.7;
export const GLYPH_COMPLEX_STROKE = 2;
export const GLYPH_EVENT_PENTAGON_R = 5.2;
export const GLYPH_EVENT_OUTER_R = 10;
export const GLYPH_EVENT_INNER_R = 8;
export const GLYPH_EVENT_STROKE = 1.2;

/** Event trigger/result glyphs (error bolt, cancel ×, link, timer, conditional). */
export const EVENT_CANCEL_ARM = 6;
export const EVENT_CANCEL_STROKE = 2.6;
export const EVENT_TIMER_R = 7;
export const EVENT_TIMER_STROKE = 1.3;
export const EVENT_CONDITIONAL_W = 10;
export const EVENT_CONDITIONAL_H = 13;
export const EVENT_CONDITIONAL_STROKE = 1.2;

// ── Activity markers (loop / multi-instance / subprocess) ───────────────────

export const MARKER_LOOP_R = 5;
export const MARKER_LOOP_STROKE = 1.4;
/** Multi-instance bars: three bars of half-length BAR, spaced GAP apart. */
export const MARKER_MULTI_BAR = 5;
export const MARKER_MULTI_GAP = 4;
export const MARKER_MULTI_STROKE = 1.6;
/** Collapsed-subprocess `⊞` box. */
export const MARKER_SUBPROCESS_SIZE = 12;
export const MARKER_SUBPROCESS_STROKE = 1.3;

// ── Type sizes & weight ─────────────────────────────────────────────────────

/** Task title. */
export const FONT_TITLE = 11;
/** Gateway/event/edge/owner/annotation labels. */
export const FONT_LABEL = 10;
/** Diff-detail chip & annotation owner. */
export const FONT_SMALL = 9;
/** Frame label weight (`font-medium`). */
export const FONT_WEIGHT_MEDIUM = 500;

// ── Single-line label PIXEL budgets ─────────────────────────────────────────
// Every BPMN label is one line, ellipsized to the width its box actually has
// (`fitLabel`). A char count can't do this job: 22 Cyrillic glyphs are ~30%
// wider than 22 Latin ones and overflowed the task box.

/** Task label — the box less its side padding. */
export const LABEL_W_TASK = TASK_W - 16;
/** Owner chip — left-aligned over the task, so the box width is the budget. */
export const LABEL_W_OWNER = TASK_W;
/** Gateway / event labels — centred in their grid column. */
export const LABEL_W_GATEWAY = CW - 20;
export const LABEL_W_EVENT = CW - 20;
/** Annotation text — its column less the bracket + text inset. */
export const LABEL_W_ANNOTATION = CW - 40;
/** Frame title strip — frames span at least one column, usually more. */
export const LABEL_W_FRAME = 2 * CW - 40;
/** Edge label — centred between two node centres, one column apart. */
export const LABEL_W_EDGE = CW - 60;
/** Diff-detail chip under a task. */
export const LABEL_W_DIFF_DETAIL = TASK_W;
/** System cylinder label — the cylinder less its side padding. */
export const LABEL_W_SYSTEM = SYSTEM_W - 8;
/** Dangling-engine badge value — the badge less its side padding. */
export const LABEL_W_ENGINE = ENGINE_BADGE_W - 8;

/**
 * Aggregate of every BPMN metric, for call sites that prefer a namespaced
 * object. The named consts above remain the tree-shakeable primary export.
 */
export const BPMN_METRICS = {
  CW,
  CH,
  PAD,
  TASK_W,
  TASK_H,
  TASK_RADIUS,
  NODE_STROKE,
  NODE_STROKE_ACTIVE,
  TASK_LABEL_DY,
  OWNER_CHIP_DY,
  INBOUND_INSET_X,
  INBOUND_INSET_Y,
  INBOUND_W,
  INBOUND_H,
  INBOUND_STROKE,
  COMPENSATION_MARKER_DY,
  SUBPROCESS_MARKER_DY,
  GATE_R,
  GATE_LABEL_DY,
  EVENT_R,
  EVENT_STROKE,
  EVENT_STROKE_OWNER,
  EVENT_STROKE_THICK,
  EVENT_DOUBLE_RING_INSET,
  EVENT_DOUBLE_RING_STROKE,
  EVENT_TERMINATE_INSET,
  EVENT_LABEL_DY,
  ANNOTATION_BRACKET_W,
  ANNOTATION_STROKE,
  ANNOTATION_STROKE_ACTIVE,
  ANNOTATION_TEXT_DX,
  ANNOTATION_TEXT_DY,
  ANNOTATION_OWNER_DY,
  FRAME_INSET_X,
  FRAME_INSET_Y,
  FRAME_INSET_W,
  FRAME_INSET_H,
  FRAME_RADIUS,
  FRAME_LABEL_DX,
  FRAME_LABEL_DY,
  FRAME_MARKER_DY,
  FRAME_DASH_DIST,
  FRAME_DASH_DEFAULT,
  EDGE_STROKE,
  EDGE_ELBOW,
  ASSOC_ANCHOR_DX,
  ASSOC_ANCHOR_DY,
  DROP_ELBOW_DIVISOR,
  REENTRY_MIN_LIFT,
  REENTRY_LIFT_DIVISOR,
  DEFAULT_SLASH_DX1,
  DEFAULT_SLASH_DY1,
  DEFAULT_SLASH_DX2,
  DEFAULT_SLASH_DY2,
  EDGE_LABEL_DY,
  EDGE_DASH,
  ARROW_VIEWBOX,
  ARROW_REF_X,
  ARROW_REF_Y,
  ARROW_MARKER_SIZE,
  ARROW_OPEN_MARKER_SIZE,
  ARROW_OPEN_STROKE,
  GLYPH_XOR_ARM,
  GLYPH_XOR_STROKE,
  GLYPH_AND_ARM,
  GLYPH_AND_STROKE,
  GLYPH_OR_R,
  GLYPH_OR_STROKE,
  GLYPH_COMPLEX_ARM,
  GLYPH_COMPLEX_DIAG,
  GLYPH_COMPLEX_STROKE,
  GLYPH_EVENT_PENTAGON_R,
  GLYPH_EVENT_OUTER_R,
  GLYPH_EVENT_INNER_R,
  GLYPH_EVENT_STROKE,
  EVENT_CANCEL_ARM,
  EVENT_CANCEL_STROKE,
  EVENT_TIMER_R,
  EVENT_TIMER_STROKE,
  EVENT_CONDITIONAL_W,
  EVENT_CONDITIONAL_H,
  EVENT_CONDITIONAL_STROKE,
  MARKER_LOOP_R,
  MARKER_LOOP_STROKE,
  MARKER_MULTI_BAR,
  MARKER_MULTI_GAP,
  MARKER_MULTI_STROKE,
  MARKER_SUBPROCESS_SIZE,
  MARKER_SUBPROCESS_STROKE,
  FONT_TITLE,
  FONT_LABEL,
  FONT_SMALL,
  LABEL_W_TASK,
  LABEL_W_OWNER,
  LABEL_W_GATEWAY,
  LABEL_W_EVENT,
  LABEL_W_ANNOTATION,
  LABEL_W_FRAME,
  LABEL_W_EDGE,
  LABEL_W_DIFF_DETAIL,
  LABEL_W_SYSTEM,
  LABEL_W_ENGINE,
  FONT_WEIGHT_MEDIUM,
} as const;
